import { apiFetch } from "./api";

/**
 * Web Push over VAPID — SPEC §11.
 *
 * On iOS this only works once the PWA is installed, which is why the A2HS flow
 * gates it. `vapidKeyVersion` is stored so a future key rotation can trigger a
 * silent re-subscribe rather than leaving devices on a dead key.
 */

const VERSION_KEY = "tanzeel.vapidKeyVersion";

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "=",
  );
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

const encodeKey = (buffer: ArrayBuffer | null): string => {
  if (buffer === null) return "";
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

export function pushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** True on iOS Safari, which never fires `beforeinstallprompt`. */
export function isIos(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

const INSTALLED_KEY = "tanzeel.installed";

/**
 * True only when *this window* was launched from the home-screen icon.
 * This is what iOS requires before it will deliver push.
 */
export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator &&
      (navigator as { standalone?: boolean }).standalone === true)
  );
}

/**
 * Whether the app is installed on the device at all.
 *
 * Deliberately NOT the same question as `isStandalone()`. Someone who installs
 * and then keeps using the browser tab is still in `display-mode: browser`, so
 * treating standalone as the install test nags them forever. There is no web
 * API that answers this directly, so the one-shot `appinstalled` event is
 * persisted instead.
 */
export function isInstalled(): boolean {
  if (isStandalone()) return true;
  try {
    return localStorage.getItem(INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
}

export function markInstalled(): void {
  try {
    localStorage.setItem(INSTALLED_KEY, "1");
  } catch {
    // Private mode with storage denied — the banner reappearing is a far
    // smaller problem than throwing here.
  }
}

export interface SubscribeResult {
  ok: boolean;
  reason?: string;
}

export async function subscribeToPush(): Promise<SubscribeResult> {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  // iOS exposes push only to a window launched from the home-screen icon —
  // having installed it is not enough if you are still in a Safari tab.
  if (isIos() && !isStandalone()) return { ok: false, reason: "open_installed" };

  const config = await apiFetch<{
    publicKey: string;
    keyVersion: number;
    configured: boolean;
  }>("/push/vapid-public-key");
  if (!config.configured) return { ok: false, reason: "server_not_configured" };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: "denied" };

  const registration = await navigator.serviceWorker.ready;

  // A rotation bumps keyVersion; drop the stale subscription first.
  const stored = Number(localStorage.getItem(VERSION_KEY) ?? "0");
  const existing = await registration.pushManager.getSubscription();
  if (existing !== null && stored !== config.keyVersion) {
    await existing.unsubscribe();
  }

  const subscription =
    stored === config.keyVersion && existing !== null
      ? existing
      : await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.publicKey),
        });

  await apiFetch("/push/subscriptions", {
    method: "POST",
    body: {
      endpoint: subscription.endpoint,
      p256dh: encodeKey(subscription.getKey("p256dh")),
      auth: encodeKey(subscription.getKey("auth")),
      vapidKeyVersion: config.keyVersion,
    },
  });

  localStorage.setItem(VERSION_KEY, String(config.keyVersion));
  return { ok: true };
}

/**
 * Whether alerts are already working on this device.
 *
 * The banner used to prompt regardless, because nothing ever asked. Permission
 * is granted once per origin and persists, so a device that has already
 * subscribed has nothing to be prompted about.
 */
export async function isPushEnabled(): Promise<boolean> {
  if (!pushSupported()) return false;
  if (Notification.permission !== "granted") return false;
  if (isIos() && !isStandalone()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    return (await registration.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}

/** Permission explicitly refused — re-prompting is pointless, the browser will not ask again. */
export function pushBlocked(): boolean {
  return pushSupported() && Notification.permission === "denied";
}
